import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Draggable } from 'react-beautiful-dnd';

import { Box } from '@mui/system';
import {
  Card,
  Stack,
  Switch,
  Button,
  Tooltip,
  IconButton,
  CardActions,
  CardContent,
  FormControlLabel,
} from '@mui/material';

import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import Question from './Question';
import Iconify from '../../../../components/iconify';
import { useFormContext } from '../../../../hooks/useForm';

QuestionCard.propTypes = {
  question: PropTypes.object,
  sectionId: PropTypes.string,
  index: PropTypes.number,
  departmentId: PropTypes.number,
};

export default function QuestionCard({ question, sectionId, index, departmentId }) {
  const {
    deleteQuestion: removeQuestion,
    addQuestion: handleAddQuestion,
    modifyQuestion,
  } = useFormContext();

  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();

  const [isRequired, setIsRequired] = useState(question.isRequired ?? false);

  const handleToggleRequired = () => {
    const newVal = !isRequired;
    setIsRequired(newVal);
    modifyQuestion(question.id, { ...question, isRequired: newVal });
  };

  return (
    <Draggable draggableId={question.id} index={index}>
      {(provided) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
        >
          <Card
            sx={{
              minWidth: !isSmallScreen && 275,
              mt: 3,
              mb: 3,
              boxShadow: '0 8px 16px 0 rgba(0,0,0,0.08)',
              borderRadius: '24px',
              border: '1px solid rgba(0,0,0,0.05)',
              bgcolor: 'background.paper',
            }}
            raised
          >
            <CardContent>
              <Question
                question={question}
                sectionId={sectionId}
                departmentId={departmentId}
              />
            </CardContent>

            <CardActions
              sx={{
                p: 2,
                bgcolor: 'rgba(145, 158, 171, 0.04)',
                borderTop: '1px solid rgba(0,0,0,0.05)',
                justifyContent: 'space-between',
                flexWrap: isSmallScreen ? 'wrap' : 'nowrap',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Iconify icon="eva:plus-fill" />}
                  onClick={() => handleAddQuestion(sectionId)}
                  sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                >
                  {t('pages.formPage.addQuestion')}
                </Button>
                <FormControlLabel
                  label={t('pages.formPage.required')}
                  control={
                    <Switch
                      checked={isRequired}
                      onChange={handleToggleRequired}
                      size="small"
                      color="primary"
                    />
                  }
                  sx={{
                    ml: 1,
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: 'text.secondary',
                    },
                  }}
                />
              </Stack>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title={t('buttons.delete')}>
                  <IconButton
                    onClick={() => removeQuestion(sectionId, question.id)}
                    sx={{ color: 'error.main' }}
                  >
                    <Iconify icon="eva:trash-2-fill" width={20} />
                  </IconButton>
                </Tooltip>
              </Box>
            </CardActions>
          </Card>
        </div>
      )}
    </Draggable>
  );
}
